import { AuthService } from '../../auth/services/auth.service';
import { StorageService } from '../../auth/services/storage.service';
import { ApiError } from '../../auth/types/auth.types';

// Variable global para manejar el evento de PASSWORD_CHANGE_REQUIRED
let globalPasswordChangeHandler: ((error: any) => void) | null = null;

// Variable global para manejar la navegación
let globalNavigateHandler: ((path: string) => void) | null = null;

/**
 * Establece el handler global para PASSWORD_CHANGE_REQUIRED
 */
export const setGlobalPasswordChangeHandler = (handler: (error: any) => void) => {
  globalPasswordChangeHandler = handler;
  
  // Exponer globalmente para testing en desarrollo
  if (process.env.NODE_ENV === 'development') {
    (window as any).__globalPasswordChangeHandler = handler;
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Global password change handler configured');
  }
};

/**
 * Establece el handler global para navegación
 */
export const setGlobalNavigateHandler = (handler: (path: string) => void) => {
  globalNavigateHandler = handler;
  
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Global navigate handler configured');
  }
};

const ongoingRequests = new Set<string>();

/**
 * Interceptor global de fetch para manejar errores de autenticación y PASSWORD_CHANGE_REQUIRED
 */
export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestKey = `${input.toString()}-${JSON.stringify(init)}`;
    
    try {
      const response = await originalFetch(input, init);
      if (!response.ok) {
        const responseClone = response.clone();
        
        try {
          const errorData: ApiError = await responseClone.json();
          
          // Manejar errores 401 (Unauthorized)
          if (response.status === 401 && !ongoingRequests.has(requestKey)) {
            ongoingRequests.add(requestKey);
            
            try {
              await AuthService.refreshToken();
              const newAccessToken = StorageService.getAccessToken();
              if (newAccessToken && init?.headers) {
                const updatedHeaders = new Headers(init.headers);
                updatedHeaders.set('Authorization', `Bearer ${newAccessToken}`);
                
                const updatedInit = {
                  ...init,
                  headers: updatedHeaders,
                };
                
                ongoingRequests.delete(requestKey);
                return originalFetch(input, updatedInit);
              }
            } catch (refreshError) {
              console.error('Token refresh failed in interceptor:', refreshError);
              AuthService.logout();
              
              // Usar el handler de navegación global si está disponible
              if (globalNavigateHandler) {
                globalNavigateHandler('/auth/login');
              } else if (typeof window !== 'undefined') {
                // Fallback a window.location.href si no hay handler
                window.location.href = '/auth/login';
              }
            } finally {
              ongoingRequests.delete(requestKey);
            }
          }
          
          // Manejar errores 403 (Forbidden) - Password Change Required
          if (response.status === 403) {
            const isPasswordChangeRequired = 
              errorData.code === 'PASSWORD_CHANGE_REQUIRED' || 
              errorData.error === 'PASSWORD_CHANGE_REQUIRED' ||
              errorData.requiresPasswordChange === true;

            if (isPasswordChangeRequired) {
              const error = new Error(errorData.message || 'Password change required') as Error & ApiError;
              error.statusCode = 403;
              error.code = 'PASSWORD_CHANGE_REQUIRED';
              if (globalPasswordChangeHandler) {
                setTimeout(() => {
                  globalPasswordChangeHandler!(error);
                }, 0);
              }
            }
          }
        } catch (parseError) {
          // Si no se puede parsear el JSON, ignorar silenciosamente
        }
      }
      return response;
    } catch (error) {
      ongoingRequests.delete(requestKey);
      throw error;
    }
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Fetch interceptor configured for authentication and password change detection');
  }
};

/**
 * Restaura la función fetch original (útil para testing)
 */
export const restoreFetch = () => {
  // Esta función se puede usar en tests para restaurar fetch
  // La implementación real requeriría guardar una referencia al fetch original
  console.warn('restoreFetch called - implement if needed for testing');
};
